import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeaturesContext";
import {
  ArrowLeft, Download, FileSpreadsheet, History, Loader2, MessageSquareText,
  RefreshCw, Send, ShieldCheck, Sparkles, Terminal, Upload,
} from "lucide-react";

const errMsg = (err, fallback) => formatApiError(err?.response?.data?.detail) || fallback;
const fmtDT = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const fmtCell = (v) => (typeof v === "number" ? v.toLocaleString("it-IT") : String(v ?? ""));

const ResultTable = ({ columns, rows, testId }) => (
  <div className="overflow-x-auto border border-border rounded-md mt-2" data-testid={testId}>
    <table className="w-full text-xs">
      <thead>
        <tr className="bg-surface-strong text-text-secondary">
          {columns.map((c) => (
            <th key={c} className="text-left font-medium px-2.5 py-1.5 whitespace-nowrap">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 10).map((r, i) => (
          <tr key={i} className="border-t border-border">
            {columns.map((c) => (
              <td key={c} className="px-2.5 py-1.5 whitespace-nowrap text-text-primary">{fmtCell(r[c])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AnswerCard = ({ msg, idx, onExport }) => (
  <div className="space-y-2" data-testid={`askfile-msg-${idx}`}>
    <div className="flex justify-end">
      <div className="bg-accent-subtle text-text-primary text-sm rounded-xl rounded-br-sm px-3.5 py-2 max-w-[85%]">
        {msg.question}
      </div>
    </div>
    <div className="bg-surface border border-border rounded-xl rounded-bl-sm px-3.5 py-3 max-w-[95%] space-y-2">
      <div className="text-sm text-text-primary whitespace-pre-wrap">{msg.explanation}</div>
      {msg.op_description && (
        <div className="flex items-start gap-2 text-[11px] text-text-muted bg-surface-strong rounded-md px-2.5 py-1.5"
             data-testid={`askfile-op-${idx}`}>
          <Terminal size={12} className="mt-0.5 flex-shrink-0" />
          <span>{msg.op_description}</span>
        </div>
      )}
      {msg.table_preview && msg.table_preview.length > 0 && (
        <>
          <ResultTable columns={msg.columns} rows={msg.table_preview} testId={`askfile-table-${idx}`} />
          <div className="flex items-center justify-between text-[11px] text-text-muted">
            <span>
              {msg.table_total > 10
                ? `Prime 10 di ${msg.table_total.toLocaleString("it-IT")} righe`
                : `${msg.table_total} ${msg.table_total === 1 ? "riga" : "righe"}`}
            </span>
            {msg.result_file_id && (
              <button type="button" onClick={() => onExport(msg)} data-testid={`askfile-export-${idx}`}
                      className="inline-flex items-center gap-1 text-accent-strong hover:underline">
                <Download size={12} /> Scarica Excel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  </div>
);

export default function AskFile() {
  const { user } = useAuth();
  const { features } = useFeatures();
  const [sid, setSid] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [preview, setPreview] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const chatEndRef = useRef(null);

  const loadHistory = useCallback(() => {
    api.get("/ai/tools/askfile/sessions").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, asking]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      let id = sid;
      if (!id) {
        const { data } = await api.post("/ai/tools/askfile/sessions");
        id = data.id;
        setSid(id);
      }
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/ai/tools/askfile/sessions/${id}/file`, fd);
      setFileInfo(data.file);
      setPreview(data.preview);
      setSuggestions(data.suggestions);
      setMessages([]);
      loadHistory();
    } catch (err) {
      toast.error(errMsg(err, "Caricamento non riuscito"));
    } finally {
      setUploading(false);
    }
  };

  const ask = async (q) => {
    const text = (q || "").trim();
    if (!text || asking || !sid) return;
    setAsking(true);
    setQuestion("");
    try {
      const { data } = await api.post(`/ai/tools/askfile/sessions/${sid}/ask`, { question: text });
      setMessages((m) => [...m, data]);
    } catch (err) {
      toast.error(errMsg(err, "Domanda non riuscita"));
    } finally {
      setAsking(false);
      inputRef.current?.focus();
    }
  };

  const exportTable = async (msg) => {
    try {
      const res = await api.get(`/ai/tools/askfile/sessions/${sid}/messages/${msg.id}/export`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "chiedi-al-file.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMsg(err, "Export non riuscito"));
    }
  };

  const reset = () => {
    setSid(null); setFileInfo(null); setPreview([]); setSuggestions([]);
    setMessages([]); setQuestion("");
  };

  const openHistorySession = async (meta) => {
    try {
      const { data } = await api.get(`/ai/tools/askfile/sessions/${meta.id}`);
      if (!data.file) { toast.info("Sessione senza file"); return; }
      setSid(data.id);
      setFileInfo(data.file);
      setPreview(data.preview || []);
      setSuggestions(data.suggestions || []);
      setMessages(data.messages || []);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err, "Impossibile aprire la sessione"));
    }
  };

  if (user && user.role !== "super_admin" && features && !features.ai_tools) {
    return <Navigate to="/assistant" replace />;
  }

  const previewCols = fileInfo ? fileInfo.columns.map((c) => c.name) : [];

  return (
    <div className="space-y-6 max-w-6xl" data-testid="askfile-page">
      <div>
        <Link to="/assistant" className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-3" data-testid="askfile-back">
          <ArrowLeft size={15} /> Assistente AI
        </Link>
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2.5">
          <MessageSquareText size={22} className="text-accent-strong" /> Chiedi al tuo file
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Carica un Excel o CSV e fai domande in italiano: conteggi, somme, duplicati, classifiche. Risposte calcolate sui tuoi dati.
        </p>
        <p className="text-xs text-text-muted mt-2 flex items-center gap-1.5" data-testid="askfile-privacy-note">
          <ShieldCheck size={13} className="text-success-strong" />
          All'AI viaggiano solo struttura, pochi campioni e aggregati calcolati — mai i dati completi. Ogni risposta mostra l'operazione eseguita.
        </p>
      </div>

      {!fileInfo ? (
        <label
          className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-border rounded-xl p-10 cursor-pointer hover:border-accent transition-colors bg-surface"
          data-testid="askfile-upload"
        >
          {uploading ? (
            <Loader2 size={28} className="animate-spin text-accent-strong" />
          ) : (
            <Upload size={28} className="text-text-muted" />
          )}
          <div className="text-sm font-medium text-text-primary">
            {uploading ? "Caricamento e lettura…" : "Carica il file (Excel o CSV)"}
          </div>
          <div className="text-xs text-text-muted">.xlsx, .xls, .csv — max 15MB, 50.000 righe</div>
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="askfile-input"
                 onChange={(e) => uploadFile(e.target.files?.[0])} disabled={uploading} />
        </label>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-2 space-y-3">
            <div className="bg-surface border border-border rounded-xl p-4" data-testid="askfile-file-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileSpreadsheet size={18} className="text-accent-strong flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">{fileInfo.filename}</div>
                    <div className="text-[11px] text-text-muted">
                      {fileInfo.rows.toLocaleString("it-IT")} righe · {fileInfo.columns.length} colonne
                    </div>
                  </div>
                </div>
                <button type="button" onClick={reset} data-testid="askfile-reset-btn"
                        className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary flex-shrink-0">
                  <RefreshCw size={12} /> Nuovo file
                </button>
              </div>
            </div>
            <div className="bg-surface border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-text-muted border-b border-border">
                Anteprima — prime {Math.min(preview.length, 50)} righe
              </div>
              <div className="overflow-auto max-h-[420px]" data-testid="askfile-preview">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-surface-strong">
                    <tr>
                      {previewCols.map((c) => (
                        <th key={c} className="text-left font-medium px-2.5 py-1.5 whitespace-nowrap text-text-secondary">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        {previewCols.map((c) => (
                          <td key={c} className="px-2.5 py-1.5 whitespace-nowrap text-text-primary">{r[c]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 bg-surface border border-border rounded-xl flex flex-col" data-testid="askfile-chat">
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] max-h-[520px]">
              {messages.length === 0 && !asking && (
                <div className="text-sm text-text-muted flex items-center gap-2 py-4">
                  <Sparkles size={15} className="text-accent-strong" />
                  Fai una domanda sul file, oppure parti da una di quelle suggerite qui sotto.
                </div>
              )}
              {messages.map((m, i) => (
                <AnswerCard key={m.id || i} msg={m} idx={i} onExport={exportTable} />
              ))}
              {asking && (
                <div className="flex items-center gap-2 text-sm text-text-muted" data-testid="askfile-thinking">
                  <Loader2 size={14} className="animate-spin" /> Sto calcolando…
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            {suggestions.length > 0 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {suggestions.map((s, i) => (
                  <button key={i} type="button" onClick={() => ask(s)} disabled={asking}
                          data-testid={`askfile-suggestion-${i}`}
                          className="text-xs border border-border rounded-full px-3 py-1.5 text-text-secondary hover:border-accent hover:text-text-primary transition-colors disabled:opacity-50">
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="p-3 border-t border-border flex items-center gap-2">
              <input
                ref={inputRef} value={question} maxLength={500}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") ask(question); }}
                placeholder='Es. "Quante righe hanno il Comune vuoto?"'
                className="wl-input flex-1" data-testid="askfile-question-input" disabled={asking}
              />
              <button type="button" onClick={() => ask(question)} disabled={asking || !question.trim()}
                      data-testid="askfile-send-btn"
                      className="wl-btn wl-btn--primary inline-flex items-center gap-1.5 px-4 disabled:opacity-50">
                {asking ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="space-y-2" data-testid="askfile-history">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-text-secondary flex items-center gap-2">
            <History size={14} /> Sessioni recenti
          </h2>
          <div className="bg-surface border border-border rounded-xl divide-y divide-border">
            {history.slice(0, 10).map((h) => (
              <button key={h.id} type="button" onClick={() => openHistorySession(h)}
                      data-testid={`askfile-history-row-${h.id}`}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-strong transition-colors">
                <div className="min-w-0">
                  <div className="text-sm text-text-primary truncate">{h.filename || "Senza file"}</div>
                  <div className="text-[11px] text-text-muted">
                    {fmtDT(h.created_at)} · {h.user_name} · {h.n_messages} {h.n_messages === 1 ? "domanda" : "domande"}
                  </div>
                </div>
                <span className="text-[11px] text-text-muted flex-shrink-0">
                  {h.rows != null ? `${h.rows.toLocaleString("it-IT")} righe` : ""}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
